<template>
  <div class="fixed inset-y-0 right-0 w-96 bg-slate-800/95 backdrop-blur-xl border-l border-slate-700/50 z-40 transform transition-transform duration-300" :class="isOpen ? 'translate-x-0' : 'translate-x-full'">
    <div class="h-full flex flex-col">
      <!-- Header -->
      <div class="p-6 border-b border-slate-700/50 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Shopping Cart</h2>
        <button @click="$emit('close')" class="text-slate-400 hover:text-white transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Cart Items -->
      <div class="flex-1 overflow-y-auto p-6">
        <div v-if="cart.length === 0" class="text-center py-12">
          <svg class="w-16 h-16 text-slate-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
          <h3 class="text-lg font-medium text-slate-300 mb-2">Your cart is empty</h3>
          <p class="text-slate-400">Add some products or courses to get started</p>
        </div>

        <div v-else class="space-y-4">
          <div v-for="item in cart" :key="`${item.type}-${item.id}`" class="bg-slate-700/30 rounded-xl p-4 border border-slate-600/30">
            <div class="flex items-start space-x-4">
              <img :src="item.image" :alt="item.name" class="w-16 h-16 object-cover rounded-lg">
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-white truncate">{{ item.name }}</h4>
                <p class="text-xs text-slate-400 mb-2">{{ item.type === 'product' ? 'Product' : 'Course' }}</p>
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="updateQuantity(item.id, item.type, item.quantity - 1)"
                      class="w-6 h-6 bg-slate-600/50 hover:bg-slate-500/50 rounded flex items-center justify-center text-white transition-colors"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                      </svg>
                    </button>
                    <span class="text-sm text-white font-medium w-8 text-center">{{ item.quantity }}</span>
                    <button 
                      @click="updateQuantity(item.id, item.type, item.quantity + 1)"
                      class="w-6 h-6 bg-slate-600/50 hover:bg-slate-500/50 rounded flex items-center justify-center text-white transition-colors"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </button>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-semibold text-white">${{ (item.price * item.quantity).toFixed(2) }}</p>
                    <button 
                      @click="removeItem(item.id, item.type)"
                      class="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-6 border-t border-slate-700/50">
        <div class="space-y-4">
          <!-- Summary -->
          <div class="space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-slate-400">Subtotal</span>
              <span class="text-white font-medium">${{ cartTotal.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-slate-400">Tax</span>
              <span class="text-white font-medium">${{ tax.toFixed(2) }}</span>
            </div>
            <div class="border-t border-slate-600/30 pt-2">
              <div class="flex justify-between text-lg font-semibold">
                <span class="text-white">Total</span>
                <span class="text-emerald-400">${{ total.toFixed(2) }}</span>
              </div>
            </div>
          </div>

          <!-- Checkout Button -->
          <button 
            @click="handleCheckout"
            :disabled="cart.length === 0 || isProcessing"
            class="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center space-x-2"
          >
            <svg v-if="isProcessing" class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <span>{{ isProcessing ? 'Processing...' : 'Proceed to Checkout' }}</span>
          </button>

          <!-- Continue Shopping -->
          <button 
            @click="$emit('close')"
            class="w-full bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white font-medium py-3 px-6 rounded-xl transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Checkout Modal -->
  <div v-if="showCheckoutModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl max-w-md w-full p-6">
      <h3 class="text-xl font-semibold text-white mb-4">Checkout</h3>
      
      <!-- Payment Method -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-slate-300 mb-2">Payment Method</label>
        <select v-model="paymentMethod" class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500">
          <option value="credit_card">Credit Card</option>
          <option value="paypal">PayPal</option>
          <option value="bank_transfer">Bank Transfer</option>
        </select>
      </div>

      <!-- Shipping Address (for products) -->
      <div v-if="hasProducts" class="mb-4">
        <label class="block text-sm font-medium text-slate-300 mb-2">Shipping Address</label>
        <textarea 
          v-model="shippingAddress" 
          placeholder="Enter your shipping address..."
          class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
          rows="3"
        ></textarea>
      </div>

      <!-- Order Summary -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-slate-300 mb-2">Order Summary</h4>
        <div class="space-y-2 text-sm">
          <div v-for="item in cart" :key="`${item.type}-${item.id}`" class="flex justify-between">
            <span class="text-slate-400">{{ item.name }} (x{{ item.quantity }})</span>
            <span class="text-white">${{ (item.price * item.quantity).toFixed(2) }}</span>
          </div>
          <div class="border-t border-slate-600/30 pt-2">
            <div class="flex justify-between font-semibold">
              <span class="text-white">Total</span>
              <span class="text-emerald-400">${{ total.toFixed(2) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex space-x-3">
        <button 
          @click="showCheckoutModal = false"
          class="flex-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white font-medium py-3 px-6 rounded-xl transition-colors"
        >
          Cancel
        </button>
        <button 
          @click="confirmOrder"
          :disabled="isProcessing"
          class="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center space-x-2"
        >
          <svg v-if="isProcessing" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <span>{{ isProcessing ? 'Processing...' : 'Confirm Order' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeService, type CartItem } from '../../services/storeService'

// Props
interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  close: []
  orderComplete: [order: any]
}>()

// Reactive data
const showCheckoutModal = ref(false)
const isProcessing = ref(false)
const paymentMethod = ref('credit_card')
const shippingAddress = ref('')

// Computed properties
const cart = computed(() => storeService.getCart().value)
const cartTotal = computed(() => storeService.getCartTotal().value)
const tax = computed(() => cartTotal.value * 0.08) // 8% tax
const total = computed(() => cartTotal.value + tax.value)

const hasProducts = computed(() => {
  return cart.value.some(item => item.type === 'product')
})

// Methods
const updateQuantity = (itemId: number, type: 'product' | 'course', quantity: number) => {
  storeService.updateQuantity(itemId, type, quantity)
}

const removeItem = (itemId: number, type: 'product' | 'course') => {
  storeService.removeFromCart(itemId, type)
}

const handleCheckout = () => {
  if (cart.value.length === 0) return
  showCheckoutModal.value = true
}

const confirmOrder = async () => {
  if (cart.value.length === 0) return
  
  isProcessing.value = true
  
  try {
    const order = await storeService.processPayment(
      paymentMethod.value, 
      hasProducts.value ? shippingAddress.value : undefined
    )
    
    showCheckoutModal.value = false
    emit('orderComplete', order)
    emit('close')
    
    // Show success message
    storeService.showNotification('Order completed successfully!')
  } catch (error) {
    console.error('Payment error:', error)
    storeService.showNotification('Payment failed. Please try again.')
  } finally {
    isProcessing.value = false
  }
}

// Watch for cart changes
watch(cart, (newCart) => {
  if (newCart.length === 0 && showCheckoutModal.value) {
    showCheckoutModal.value = false
  }
}, { deep: true })
</script>

<style scoped>
/* Custom scrollbar for cart items */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(16, 185, 129, 0.5);
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(16, 185, 129, 0.7);
}
</style>
